/**
 * WelcomeModal Component (AC-668)
 *
 * Modal shown to new users on first login to introduce the tutorial system.
 * Provides options to start the tutorial or skip for now.
 *
 * Technical Design Reference: docs/technical/TDD-AC-661-tutorial-system.md
 *
 * Features:
 * - Welcome message with Agent Commander overview
 * - Estimated time (5-10 minutes)
 * - Learning outcomes (Operations, Agents, Workflows)
 * - Start Tutorial and Skip buttons
 * - Accessible modal with proper ARIA attributes
 * - Focus trap within modal
 * - Does NOT close on Escape or backdrop click (intentional for onboarding)
 *
 * @see AC-668 - WelcomeModal component
 * @see AC-661 - Epic: Interactive Tutorial System
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { WelcomeModalProps } from '../../types/tutorial';
import AnimatedLogo from '../common/AnimatedLogo';

/**
 * WelcomeModal - Introduction modal for new users.
 */
const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onStart, onSkip }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Focus management - focus Start button on open.
   * Also trigger salute animation on the AnimatedLogo.
   */
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        startButtonRef.current?.focus();
        window.dispatchEvent(new Event('agent-salute'));
      }, 0);
    }
  }, [isOpen]);

  /**
   * Focus trap - keep focus within modal.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (event: FocusEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        startButtonRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusTrap);
    return () => {
      document.removeEventListener('focusin', handleFocusTrap);
    };
  }, [isOpen]);

  /**
   * Handle keyboard events - intentionally does NOT close on Escape.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Intentionally do nothing on Escape for onboarding modal
      // This encourages users to make an explicit choice
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <>
      {/* Backdrop overlay - does not close on click */}
      <div
        data-testid="modal-backdrop"
        className="fixed inset-0 bg-black/50 z-50 overlay"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-modal-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          className="bg-white dark:bg-cap-dark rounded-xl shadow-2xl max-w-[calc(100%-2rem)] sm:max-w-md w-full p-4 sm:p-6 transform transition-all duration-300 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Welcome Icon */}
          <div className="flex justify-center mb-4" data-testid="welcome-icon">
            <AnimatedLogo size={64} />
          </div>

          {/* Title */}
          <h2
            id="welcome-modal-title"
            className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-warm-white mb-2"
          >
            Welcome to Agent Commander!
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
            This quick tutorial will guide you through building your first AI workflow
            with workflow capabilities.
          </p>

          {/* Time estimate */}
          <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Takes about 5-10 minutes</span>
          </div>

          {/* Learning outcomes */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              You will learn how to:
            </p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-center">
                <span className="w-4 h-4 mr-2 flex items-center justify-center" aria-hidden="true">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                </span>
                Create a Project to organize your work
              </li>
              <li className="flex items-center">
                <span className="w-4 h-4 mr-2 flex items-center justify-center" aria-hidden="true">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                </span>
                Configure an AI Agent
              </li>
              <li className="flex items-center">
                <span className="w-4 h-4 mr-2 flex items-center justify-center" aria-hidden="true">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                </span>
                Build and run a Workflow
              </li>
            </ul>
          </div>

          {/* Restart later note */}
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
            You can restart this tutorial later from the <span className="font-semibold text-gray-700 dark:text-gray-300">Bootcamp</span>.
          </p>

          {/* Action buttons */}
          <div className="flex flex-col space-y-2">
            <button
              ref={startButtonRef}
              type="button"
              onClick={onStart}
              className="w-full min-h-[48px] py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 primary btn-primary"
            >
              Start Tutorial
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="w-full min-h-[48px] py-2 px-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:ring-gray-600 rounded-lg"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Render via portal for proper z-index layering
  return createPortal(modalContent, document.body);
};

export default WelcomeModal;
